import { TrackProductService } from '@analytics/events';
import { Injectable, Injector } from '@angular/core';
import { getFieldValue } from '@core/jss-utils';
import { mapProductToCommerce } from '@innogy/become-a-customer/shared';
import {
  ActivateFormStepAction,
  SubmitFormStepAction,
} from '@innogy/progressive-ngrx-forms';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, type Action } from '@ngrx/store';
import { SetValueAction } from 'ngrx-forms';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs';

import { ORDER_ADDRESS_FORM_ID } from '../address';
import {
  initOrderCrossSell,
  orderCrossSellEnabled,
} from './order-cross-sell.actions';
import {
  hasSelectedProductFormControlId,
  ORDER_CROSS_SELL_FORM_ID,
} from './order-cross-sell.reducer';
import {
  getOrderCrossSellProduct,
  getOrderCrossSellState,
} from './order-cross-sell.selector';

@Injectable()
export class OrderCrossSellEffects {
  constructor(
    private readonly injector: Injector,
    private readonly store$: Store,
    private readonly actions$: Actions
  ) {}

  orderCrossSellState$ = this.store$.select(getOrderCrossSellState);
  orderCrossSellProduct$ = this.store$.select(getOrderCrossSellProduct);

  public readonly enableCrossSellAfterInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initOrderCrossSell),
      withLatestFrom(this.orderCrossSellState$),
      switchMap(
        ([
          { deviceInfo, thuisvoordeelAddonEnabled },
          { previouslyEnabled, enabled, rendering, progressiveForm },
        ]) => {
          const isCustomerTypeEnabled = getFieldValue(
            rendering,
            'EnableCustomerTypeStep',
            false
          );
          const actions: Action[] = [
            // Notify other modules that cross-sell has been enabled
            orderCrossSellEnabled({ enabled, isCustomerTypeEnabled }),
          ];

          const newStepVariant = getFieldValue(
            rendering,
            'CrossSellUseNewDesign',
            false
          );

          const hiddenForMobile =
            !deviceInfo.isDesktop && thuisvoordeelAddonEnabled;
          if (
            !previouslyEnabled &&
            enabled &&
            newStepVariant &&
            !hiddenForMobile
          ) {
            // The new variant must select an option, therefore the initial value is removed if the state is newly enabled
            actions.push(
              new SetValueAction(
                progressiveForm.formState.controls.hasSelectedProduct.id,
                undefined
              )
            );
          }

          if (
            !previouslyEnabled &&
            enabled &&
            !(newStepVariant && thuisvoordeelAddonEnabled)
          ) {
            // If the thuisvoordeel addon is not enabled, the default value is false, otherwise the validator will reject it
            actions.push(
              new SetValueAction(
                progressiveForm.formState.controls.hasSelectedLoyaltyProgram.id,
                false
              )
            );
          }

          return actions;
        }
      )
    )
  );

  public readonly trackProductImpression$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SubmitFormStepAction | ActivateFormStepAction>(
          SubmitFormStepAction.TYPE,
          ActivateFormStepAction.TYPE
        ),
        filter(
          (action) =>
            (action.type === SubmitFormStepAction.TYPE &&
              action.stepId === ORDER_ADDRESS_FORM_ID) ||
            (action.type === ActivateFormStepAction.TYPE &&
              action.stepId === ORDER_CROSS_SELL_FORM_ID)
        ),
        withLatestFrom(this.orderCrossSellProduct$),
        tap(([_, product]) => {
          if (product) {
            /**
             * WTA-17: resolve injector context
             */
            const trackProductService = this.injector.get(TrackProductService);
            trackProductService.trackImpression(mapProductToCommerce(product));
            trackProductService.trackDetail(mapProductToCommerce(product));
          }
        })
      ),
    { dispatch: false }
  );

  public readonly trackProductClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SetValueAction<boolean>>(SetValueAction.TYPE),
        filter(
          (action) =>
            action.controlId === hasSelectedProductFormControlId &&
            action.value === true
        ),
        withLatestFrom(this.orderCrossSellProduct$),
        tap(([_, product]) => {
          if (product) {
            /**
             * WTA-17: resolve injector context
             */
            const productService = this.injector.get(TrackProductService);
            productService.trackSelection(mapProductToCommerce(product));
          }
        })
      ),
    { dispatch: false }
  );
}
